import { useRef, useState, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { BCup, BCupRelation } from '../types'

interface Props {
  cup: BCup
  parentRelations: BCupRelation[]  // relations where cup is the child (belongs-to)
  childRelations: BCupRelation[]   // relations where cup is the parent (has-many)
  allCups: BCup[]
}

function bezier(from: { x: number; y: number }, to: { x: number; y: number }) {
  const cx = (from.x + to.x) / 2
  return `M ${from.x} ${from.y} C ${cx} ${from.y} ${cx} ${to.y} ${to.x} ${to.y}`
}

function sideMid(el: HTMLElement, container: HTMLElement, side: 'left' | 'right') {
  const er = el.getBoundingClientRect()
  const cr = container.getBoundingClientRect()
  return {
    x: side === 'left' ? er.left - cr.left : er.right - cr.left,
    y: er.top + er.height / 2 - cr.top,
  }
}

type Edge = { d: string; midX: number; midY: number; label?: string; amber: boolean }

export function CupRelationsGraph({ cup, parentRelations, childRelations, allCups }: Props) {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const centerRef    = useRef<HTMLDivElement>(null)
  const parentRefs   = useRef(new Map<string, HTMLDivElement>())
  const childRefs    = useRef(new Map<string, HTMLDivElement>())
  const [edges, setEdges]     = useState<Edge[]>([])
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0 })

  const cupById = new Map(allCups.map(c => [c.id, c]))

  useLayoutEffect(() => {
    const ctr = containerRef.current
    const center = centerRef.current
    if (!ctr || !center) return

    setSvgSize({ w: ctr.offsetWidth, h: ctr.offsetHeight })

    const next: Edge[] = []

    for (const [relId, el] of parentRefs.current) {
      const rel = parentRelations.find(r => r.id === relId)
      if (!rel) continue
      const from = sideMid(el, ctr, 'right')
      const to   = sideMid(center, ctr, 'left')
      next.push({ d: bezier(from, to), midX: (from.x + to.x) / 2, midY: (from.y + to.y) / 2, label: rel.label, amber: true })
    }

    for (const [relId, el] of childRefs.current) {
      const rel = childRelations.find(r => r.id === relId)
      if (!rel) continue
      const from = sideMid(center, ctr, 'right')
      const to   = sideMid(el, ctr, 'left')
      next.push({ d: bezier(from, to), midX: (from.x + to.x) / 2, midY: (from.y + to.y) / 2, label: rel.label, amber: false })
    }

    setEdges(next)
  }, [parentRelations, childRelations])

  const nodeBase = 'bg-white border rounded-lg px-3 py-2.5 shadow-sm cursor-pointer select-none transition-all hover:shadow-md'

  if (!parentRelations.length && !childRelations.length) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-ink-dim">
        No relations defined for this cup. Use the Relations button to add some.
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="relative rounded-xl border border-paper-border bg-paper-sunken/60 p-8"
      style={{ minHeight: 160 }}
    >
      {svgSize.w > 0 && (
        <svg
          width={svgSize.w}
          height={svgSize.h}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}
        >
          <defs>
            <marker id="crg-arr-amber" markerWidth={8} markerHeight={6} refX={8} refY={3} orient="auto">
              <polygon points="0 0,8 3,0 6" fill="#f79009" />
            </marker>
            <marker id="crg-arr-green" markerWidth={8} markerHeight={6} refX={8} refY={3} orient="auto">
              <polygon points="0 0,8 3,0 6" fill="#12b76a" />
            </marker>
          </defs>
          {edges.map((e, i) => (
            <g key={i}>
              <path
                d={e.d}
                fill="none"
                stroke={e.amber ? '#f79009' : '#12b76a'}
                strokeWidth={1.5}
                markerEnd={`url(#crg-arr-${e.amber ? 'amber' : 'green'})`}
              />
              {e.label && (
                <text
                  x={e.midX}
                  y={e.midY + 4.5}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#98a2b3"
                  fontFamily="ui-monospace, monospace"
                >
                  {e.label}
                </text>
              )}
            </g>
          ))}
        </svg>
      )}

      <div className="flex items-center justify-center gap-20 relative z-10">
        {parentRelations.length > 0 && (
          <div className="flex flex-col gap-3">
            {parentRelations.map(rel => {
              const parentCup = cupById.get(rel.parentCupId)
              return (
                <div
                  key={rel.id}
                  ref={el => { if (el) parentRefs.current.set(rel.id, el); else parentRefs.current.delete(rel.id) }}
                  className={`${nodeBase} border-amber-200 w-44`}
                  onClick={() => navigate(`/brainer/cups/${rel.parentCupId}`)}
                >
                  <div className="text-sm font-semibold text-ink truncate">{parentCup?.title ?? '…'}</div>
                  <div className="text-[10px] text-amber-500 mt-0.5">belongs to</div>
                </div>
              )
            })}
          </div>
        )}

        <div
          ref={centerRef}
          className="bg-white border-2 border-accent rounded-lg px-3 py-3 shadow-md w-48 shrink-0"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-2 h-2 rounded-full bg-accent shrink-0" />
            <span className="text-sm font-bold text-ink truncate">{cup.title}</span>
          </div>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cup.disposable ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
            {cup.disposable ? 'Disposable' : 'Persistent'}
          </span>
          {cup.description && (
            <p className="text-theme-xs text-ink-dim mt-1.5 line-clamp-2">{cup.description}</p>
          )}
        </div>

        {childRelations.length > 0 && (
          <div className="flex flex-col gap-3">
            {childRelations.map(rel => {
              const childCup = cupById.get(rel.childCupId)
              return (
                <div
                  key={rel.id}
                  ref={el => { if (el) childRefs.current.set(rel.id, el); else childRefs.current.delete(rel.id) }}
                  className={`${nodeBase} border-emerald-200 w-44`}
                  onClick={() => navigate(`/brainer/cups/${rel.childCupId}`)}
                >
                  <div className="text-sm font-semibold text-ink truncate">{childCup?.title ?? '…'}</div>
                  <div className="text-[10px] text-emerald-600 mt-0.5">has many</div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
